import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Car, Wrench, ShieldCheck, Clock, ChevronDown, Phone,
  Calendar, ShoppingCart, FileText, Info, ArrowRight, Gauge
} from 'lucide-react';

/**
 * Desktop navigation.
 *
 * Overflow strategy: the dropdown is a FULL-BLEED panel pinned to the viewport
 * (`fixed left-0 right-0`) with its content constrained to the page container.
 * Because it spans edge-to-edge, it can never push past the right edge the way a
 * trigger-anchored `max-content` panel does — overflow is structurally impossible
 * at any width. Its vertical offset is measured from the live header bottom so it
 * tracks the collapsing top bar / scroll state.
 */
const MegaMenu = ({ onBookService }) => {
  const [activeMenu, setActiveMenu] = useState(null);
  const [panelTop, setPanelTop] = useState(96);
  const timeoutRef = useRef(null);
  const navRef = useRef(null);
  const location = useLocation();

  const measureTop = () => {
    const header = navRef.current?.closest('header');
    if (header) setPanelTop(header.getBoundingClientRect().bottom);
  };

  const openMenu = (menu) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    measureTop();
    setActiveMenu(menu);
  };

  const scheduleClose = () => {
    timeoutRef.current = setTimeout(() => setActiveMenu(null), 180);
  };

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  // Close on route change + Escape
  useEffect(() => { setActiveMenu(null); }, [location.pathname]);
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && setActiveMenu(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const isActive = (href) =>
    href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);

  const menus = {
    vehicles: {
      title: 'Vehicles',
      columns: [
        {
          eyebrow: '01', heading: 'Browse Inventory', icon: Car,
          items: [
            { name: 'All Vehicles', href: '/inventory', desc: 'Our full lineup' },
            { name: 'BMW', href: '/inventory/bmw', desc: 'Shop BMW' },
            { name: 'Audi', href: '/inventory/audi', desc: 'Shop Audi' },
            { name: 'Volkswagen', href: '/inventory/volkswagen', desc: 'Shop VW' },
          ],
        },
        {
          eyebrow: '02', heading: 'Buy & Finance', icon: ShoppingCart,
          items: [
            { name: 'Get Pre-Qualified', href: '/pre-approval', tag: 'SOFT PULL', desc: 'No credit impact' },
            { name: 'Finance Application', href: '/finance-application', desc: 'Apply in minutes' },
            { name: 'Trade-In Value', href: '/trade-in', desc: "What's yours worth?" },
            { name: 'Parts & Accessories', href: '/parts', desc: 'OBD tools & parts' },
          ],
        },
      ],
      cta: { to: '/inventory', label: 'View All Inventory' },
    },
    services: {
      title: 'Services',
      columns: [
        {
          eyebrow: '01', heading: 'European Specialists', icon: ShieldCheck,
          items: [
            { name: 'BMW Repair', href: '/services/european-car-repair/bmw' },
            { name: 'Mercedes-Benz', href: '/services/european-car-repair/mercedes' },
            { name: 'Audi', href: '/services/european-car-repair/audi' },
            { name: 'Porsche', href: '/services/european-car-repair/porsche' },
            { name: 'Volkswagen', href: '/services/european-car-repair/volkswagen' },
            { name: 'Land Rover', href: '/services/european-car-repair/land-rover' },
          ],
        },
        {
          eyebrow: '02', heading: 'Repair & Maintenance', icon: Wrench,
          items: [
            { name: 'Check Engine Light', href: '/services/diagnostic-check-engine-light', desc: 'Free scan' },
            { name: 'Transmission', href: '/services/transmission-repair' },
            { name: 'Suspension', href: '/services/suspension-repair' },
            { name: 'Clutch Repair', href: '/services/clutch-repair' },
            { name: 'Wheel & Tire', href: '/services/wheel-and-tire' },
          ],
        },
      ],
      cta: { to: '/service', label: 'Book Service' },
    },
    resources: {
      title: 'Resources',
      columns: [
        {
          eyebrow: '01', heading: 'Read & Learn', icon: FileText,
          items: [
            { name: 'Blog & Guides', href: '/blog', desc: 'Tips from the bay' },
            { name: 'Porsche PSM Failure', href: '/blog/porsche-psm-failure-guide', tag: 'NEW' },
            { name: 'VW EPC Light Fix', href: '/blog/fix-vw-epc-light', tag: 'NEW' },
          ],
        },
        {
          eyebrow: '02', heading: 'Company', icon: Info,
          items: [
            { name: 'About Us', href: '/about', desc: 'Our story' },
            { name: 'Warranty', href: '/warranty', desc: 'Our guarantee' },
            { name: 'Contact', href: '/contact', desc: 'Get in touch' },
            { name: 'My Payments', href: '/my-payments', desc: 'Pay online' },
          ],
        },
      ],
      cta: { to: '/contact', label: 'Talk to Us' },
    },
  };

  const flatLinks = [
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  return (
    <nav ref={navRef} className="relative flex items-center gap-x-0.5 xl:gap-x-1">
      {Object.entries(menus).map(([key, menu]) => {
        const open = activeMenu === key;
        return (
          <div key={key} onMouseEnter={() => openMenu(key)} onMouseLeave={scheduleClose}>
            <button
              onClick={() => (open ? setActiveMenu(null) : openMenu(key))}
              className={`group relative flex items-center gap-1 px-3 py-2 text-[15px] font-medium tracking-tight transition-colors ${open ? 'text-red-600' : 'text-gray-700 hover:text-gray-900'
                }`}
              aria-expanded={open}
            >
              <span>{menu.title}</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
              {/* spark underline */}
              <span
                className={`pointer-events-none absolute left-3 right-3 -bottom-0.5 h-0.5 origin-left bg-red-600 transition-transform duration-300 ${open ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
              />
            </button>
          </div>
        );
      })}

      {flatLinks.map((link) => (
        <Link
          key={link.href}
          to={link.href}
          className={`group relative px-3 py-2 text-[15px] font-medium tracking-tight transition-colors ${isActive(link.href) ? 'text-red-600' : 'text-gray-700 hover:text-gray-900'
            }`}
        >
          {link.name}
          <span
            className={`pointer-events-none absolute left-3 right-3 -bottom-0.5 h-0.5 origin-left bg-red-600 transition-transform duration-300 ${isActive(link.href) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`}
          />
        </Link>
      ))}

      <button
        onClick={onBookService}
        className="ml-2 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-[15px] font-semibold text-white shadow-sm shadow-red-600/20 transition-all hover:bg-red-700 hover:shadow-md hover:shadow-red-600/30 active:scale-[0.98]"
      >
        <Calendar className="w-4 h-4" />
        <span className="whitespace-nowrap">Book Service</span>
      </button>

      {/* ---- Full-bleed dropdown panel (overflow-proof) ---- */}
      <AnimatePresence>
        {activeMenu && (
          <motion.div
            key={activeMenu}
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="fixed left-0 right-0 z-40 border-t-2 border-red-600 bg-white shadow-[0_24px_60px_-20px_rgba(0,0,0,0.35)]"
            style={{ top: panelTop }}
            onMouseEnter={() => openMenu(activeMenu)}
            onMouseLeave={scheduleClose}
          >
            <div className="container mx-auto px-6 xl:px-8">
              <div className="grid grid-cols-12 gap-8 py-8 max-h-[70vh] overflow-y-auto">
                {/* Brand rail */}
                <div className="col-span-3 flex flex-col justify-between border-r border-gray-100 pr-8">
                  <div>
                    <Gauge className="w-7 h-7 text-red-600" />
                    <h3 className="mt-4 text-2xl font-bold tracking-tight text-gray-900">
                      {menus[activeMenu].title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-gray-500">
                      Factory-trained technicians. European &amp; diesel specialists in Paradise, TX.
                    </p>
                  </div>
                  <Link
                    to={menus[activeMenu].cta.to}
                    onClick={() => setActiveMenu(null)}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-red-600 transition-colors hover:text-red-700"
                  >
                    {menus[activeMenu].cta.label}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Columns */}
                <div className="col-span-9 grid grid-cols-2 gap-x-10 gap-y-2">
                  {menus[activeMenu].columns.map((col) => (
                    <div key={col.heading}>
                      <div className="mb-4 flex items-center gap-3">
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50 text-red-600">
                          <col.icon className="h-[18px] w-[18px]" />
                        </span>
                        <div className="leading-tight">
                          <span className="block font-mono text-[11px] font-semibold tracking-widest text-red-500">
                            {col.eyebrow}
                          </span>
                          <span className="block text-sm font-bold uppercase tracking-wide text-gray-900">
                            {col.heading}
                          </span>
                        </div>
                      </div>
                      <ul className="space-y-0.5">
                        {col.items.map((item) => (
                          <li key={item.href}>
                            <Link
                              to={item.href}
                              onClick={() => setActiveMenu(null)}
                              className="group flex items-center justify-between rounded-lg px-3 py-2 transition-colors hover:bg-gray-50"
                            >
                              <span className="min-w-0">
                                <span className="flex items-center gap-2">
                                  <span className="truncate text-[15px] font-medium text-gray-800 transition-colors group-hover:text-red-600">
                                    {item.name}
                                  </span>
                                  {item.tag && (
                                    <span className="shrink-0 rounded-full bg-red-600 px-2 py-0.5 text-[10px] font-bold tracking-wider text-white">
                                      {item.tag}
                                    </span>
                                  )}
                                </span>
                                {item.desc && (
                                  <span className="block truncate text-xs text-gray-400">{item.desc}</span>
                                )}
                              </span>
                              <ArrowRight className="ml-2 h-4 w-4 shrink-0 -translate-x-1 text-red-500 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer strip */}
              <div className="flex items-center justify-between border-t border-gray-100 py-4 text-sm">
                <div className="flex items-center gap-6 text-gray-500">
                  <a href="tel:8173822316" className="flex items-center gap-2 font-medium text-gray-700 transition-colors hover:text-red-600">
                    <Phone className="h-4 w-4" /> (817) 382-2316
                  </a>
                  <span className="hidden items-center gap-2 sm:flex">
                    <Clock className="h-4 w-4" /> Mon–Fri 8AM–5PM
                  </span>
                </div>
                <Link
                  to={menus[activeMenu].cta.to}
                  onClick={() => setActiveMenu(null)}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 font-semibold text-white transition-colors hover:bg-red-600"
                >
                  {menus[activeMenu].cta.label}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default MegaMenu;
